/**
 * Domain 5: Working Memory (الذاكرة العاملة)
 * 8 Modular Template Families for retention, mental manipulation, and sequence probes
 */

import { renderOptionSvg, DrawShapes } from './svg-builder.js';

export const MEMORY_TEMPLATES = [
  // 1. Spatial Grid Memory Recall (4x4 grid target location)
  {
    id: 'memory_visual_grid_recall',
    domain: 'working_memory',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    hasMemoryPhase: true,
    generate(prng, difficulty = 2) {
      // 4x4 grid: 16 cells (rows 0..3, cols 0..3)
      const targetCount = difficulty === 1 ? 3 : difficulty === 2 ? 4 : 5;
      const allCells = Array.from({ length: 16 }, (_, i) => ({ r: Math.floor(i / 4), c: i % 4, idx: i }));
      const activeCells = prng.sample(allCells, targetCount);
      const activeIndices = activeCells.map(c => c.idx);

      // Question: Which cell of the following was ONE of the active highlighted cells?
      const targetQueryCell = activeCells[0];
      const inactiveCells = allCells.filter(c => !activeIndices.includes(c.idx));
      const distractorCells = prng.sample(inactiveCells, 3);

      const renderGridSvg = (highlightIdxs, showAll = true) => {
        let out = `<rect x="0" y="0" width="200" height="200" rx="12" fill="rgba(14, 22, 40, 0.7)" stroke="rgba(255, 255, 255, 0.15)" stroke-width="2"/>`;
        const cellS = 40;
        const gap = 6;
        const pad = 11;
        for (let r = 0; r < 4; r++) {
          for (let c = 0; c < 4; c++) {
            const idx = r * 4 + c;
            const x = pad + c * (cellS + gap);
            const y = pad + r * (cellS + gap);
            const isLit = highlightIdxs.includes(idx);
            out += `<rect x="${x}" y="${y}" width="${cellS}" height="${cellS}" rx="6" fill="${isLit ? '#83C8F0' : 'rgba(255, 255, 255, 0.05)'}" stroke="${isLit ? '#6666E8' : 'rgba(255, 255, 255, 0.1)'}" stroke-width="1.5"/>`;
          }
        }
        return `<svg class="iq-svg-memory-grid" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">${out}</svg>`;
      };

      // Memory stimulus (shown during memorization phase)
      const memoryStimulusHtml = renderGridSvg(activeIndices);

      // Probe stimulus: grid with lettered coordinates or question
      const drawSingleCellOption = (cell) => (s) => {
        const rowLabel = ['أعلى', 'وسط علوي', 'وسط سفلي', 'أسفل'][cell.r];
        const colLabel = ['عمود 1', 'عمود 2', 'عمود 3', 'عمود 4'][cell.c];
        return renderGridSvg([cell.idx]);
      };

      const allOptionsCells = prng.shuffle([
        { cell: targetQueryCell, isCorrect: true },
        { cell: distractorCells[0], isCorrect: false },
        { cell: distractorCells[1], isCorrect: false },
        { cell: distractorCells[2], isCorrect: false }
      ]);

      const correctIndex = allOptionsCells.findIndex(o => o.isCorrect);

      const options = allOptionsCells.map((o, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: o.cell ? renderGridSvg([o.cell.idx]) : '',
        text_ar: `الصف ${o.cell.r + 1} ، العمود ${o.cell.c + 1}`,
        text_en: `Row ${o.cell.r + 1}, Col ${o.cell.c + 1}`
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تذكر مواقع المربعات المضيئة الزرقاء: أي من الشبكات التالية توضح موقع أحد المربعات التي كانت مضيئة؟',
        questionText_en: 'Recall the illuminated blue cell positions: which of the following grids correctly shows ONE of the originally active cells?',
        stimulusHtml: memoryStimulusHtml,
        options,
        correctIndex,
        explanation_ar: `المربع المضيء المستهدف كان في (الصف ${targetQueryCell.r + 1}، العمود ${targetQueryCell.c + 1}).`,
        explanation_en: `The target illuminated cell was located at (Row ${targetQueryCell.r + 1}, Column ${targetQueryCell.c + 1}).`
      };
    }
  },

  // 2. Backward Digit Span (Mental Inversion)
  {
    id: 'memory_backward_digit_span',
    domain: 'working_memory',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const len = difficulty === 1 ? 4 : difficulty === 2 ? 5 : 6;
      const digits = [];
      while (digits.length < len) {
        const d = prng.int(1, 9);
        if (!digits.includes(d)) digits.push(d);
      }

      const forwardStr = digits.join(' - ');
      const backwardDigits = [...digits].reverse();
      const correctStr = backwardDigits.join(' - ');

      // Distractors: wrong reversals or swapped adjacent
      const distractor1 = [...digits].join(' - '); // Forward
      const swapped = [...backwardDigits];
      [swapped[0], swapped[1]] = [swapped[1], swapped[0]];
      const distractor2 = swapped.join(' - ');
      const distractor3 = backwardDigits.slice(1).concat(backwardDigits[0]).join(' - ');

      const allChoices = prng.shuffle([
        { str: correctStr, isCorrect: true },
        { str: distractor1, isCorrect: false },
        { str: distractor2, isCorrect: false },
        { str: distractor3, isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml = `
        <div class="iq-digit-sequence-board" role="region" aria-label="سلسلة الأرقام" dir="ltr">
          <span class="iq-digit-seq-value" dir="ltr">\u2066${forwardStr}\u2069</span>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: `\u2066${c.str}\u2069`,
        text_en: c.str
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `احفظ سلسلة الأرقام التالية في ذهنك، ثم حدد الترتيب المعكوس الدقيق لها (من الرقم الأخير إلى الأول):`,
        questionText_en: `Hold this numerical sequence in mind, then identify its exact reverse order (from last to first):`,
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `عكس السلسلة (\u2066${forwardStr}\u2069) يبدأ من الرقم الأخير وينتهي بالأول: (\u2066${correctStr}\u2069).`,
        explanation_en: `Inverting the sequence (${forwardStr}) from last digit to first yields (${correctStr}).`
      };
    }
  },

  // 3. Symbol Sequence Position Probe
  {
    id: 'memory_symbol_sequence_probe',
    domain: 'working_memory',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const symbols = ['▲', '■', '●', '★', '◆', '✚'];
      const sequence = prng.sample(symbols, 5);

      const targetPos = prng.int(2, 4); // 2nd, 3rd, or 4th position
      const correctSymbol = sequence[targetPos - 1];

      const distractors = symbols.filter(s => s !== correctSymbol).slice(0, 3);
      const allChoices = prng.shuffle([correctSymbol, ...distractors]);
      const correctIndex = allChoices.indexOf(correctSymbol);

      const posNames_ar = ['', 'الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس'];
      const posNames_en = ['', '1st', '2nd', '3rd', '4th', '5th'];

      const stimulusHtml = `
        <div class="iq-symbol-sequence-board" role="region" aria-label="تسلسل الرموز" dir="ltr">
          <div class="iq-seq-direction-guide" aria-hidden="true" dir="ltr">
            <span class="iq-seq-end-label">Left / يسار</span>
            <span class="iq-seq-arrow">──────▶</span>
            <span class="iq-seq-end-label">Right / يمين</span>
          </div>
          <div class="iq-symbol-tokens-row" dir="ltr">
            ${sequence.map((sym, i) => `
              <div class="iq-symbol-token-col">
                <span class="iq-symbol-token-idx">${i + 1}</span>
                <span class="iq-symbol-token">${sym}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;

      const options = allChoices.map((sym, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: sym,
        text_en: sym
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على الترتيب الأصلي للرموز من اليسار إلى اليمين (الموضع 1 إلى 5)، ما هو الرمز الذي كان في الموقع (${posNames_ar[targetPos]})؟`,
        questionText_en: `Based on the original sequence order from left to right (positions 1 to 5), which symbol occupied the (${posNames_en[targetPos]}) position?`,
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `بالترتيب من اليسار إلى اليمين (1 إلى 5)، الرمز الموجود في الموقع ${posNames_ar[targetPos]} (رقم ${targetPos}) هو "${correctSymbol}".`,
        explanation_en: `From left to right (1 to 5), the symbol in the ${posNames_en[targetPos]} slot (position ${targetPos}) is "${correctSymbol}".`
      };
    }
  },

  // 4. Dual Feature Binding (Shape + Color/Letter Association)
  {
    id: 'memory_dual_feature_binding',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const shapes = [
        { name_ar: 'المثلث', name_en: 'Triangle', draw: (s) => DrawShapes.triangle(s, true, '#83C8F0', '#83C8F0') },
        { name_ar: 'المربع', name_en: 'Square', draw: (s) => DrawShapes.square(s, true, '#6666E8', '#6666E8') },
        { name_ar: 'الدائرة', name_en: 'Circle', draw: (s) => DrawShapes.circle(s, true, '#10b981', '#10b981') },
        { name_ar: 'النجمة', name_en: 'Star', draw: (s) => DrawShapes.star(s, true, '#f59e0b', '#f59e0b') }
      ];

      const numbers = prng.sample([17, 24, 38, 52, 63, 79, 91], 3);
      const chosenShapes = prng.sample(shapes, 3);

      const bindings = chosenShapes.map((sh, i) => ({ shape: sh, num: numbers[i] }));
      const targetBinding = bindings[prng.int(0, 2)];

      const stimulusSvg = `<svg class="iq-svg-binding" viewBox="0 0 320 100" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="320" height="100" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        ${bindings.map((b, idx) => `
          <g transform="translate(${60 + idx * 100}, 45)">
            ${b.shape.draw(40)}
            <text x="0" y="38" class="iq-svg-text" font-size="16" font-weight="bold" text-anchor="middle">${b.num}</text>
          </g>
        `).join('')}
      </svg>`;

      const correctNum = targetBinding.num;
      const otherNumbers = numbers.filter(n => n !== correctNum);
      let distractorExtra = prng.int(20, 80);
      while (numbers.includes(distractorExtra)) {
        distractorExtra = (distractorExtra + 5) % 100 || 21;
      }

      const allChoices = prng.shuffle([
        correctNum,
        otherNumbers[0],
        otherNumbers[1],
        distractorExtra
      ]);

      const correctIndex = allChoices.indexOf(correctNum);

      const options = allChoices.map((num, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(num),
        text_en: String(num)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تذكر الاقتران بين كل شكل ورقمه: ما هو الرقم الذي كان مقترنًا بـ (${targetBinding.shape.name_ar})؟`,
        questionText_en: `Recall the pairing between each shape and its number: Which number was associated with the (${targetBinding.shape.name_en})?`,
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `كان (${targetBinding.shape.name_ar}) مقترنًا بالرقم ${correctNum}.`,
        explanation_en: `The (${targetBinding.shape.name_en}) was bound to the number ${correctNum}.`
      };
    }
  },

  // 5. Running Mental Tally Arithmetic
  {
    id: 'memory_running_tally_mental',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const start = prng.int(10, 25);
      const op1 = prng.int(3, 7);
      const op2 = prng.int(2, 6);
      const op3 = prng.int(4, 8);

      // start + op1 - op2 + op3
      const step1 = start + op1;
      const step2 = step1 - op2;
      const finalVal = step2 + op3;

      const cand = [finalVal - 2, finalVal + 3, finalVal - op2, finalVal + op1, finalVal + 5, finalVal - 4]
        .filter(v => v !== finalVal && v > 0);
      const distractors = Array.from(new Set(cand)).slice(0, 3);

      const allChoices = prng.shuffle([finalVal, ...distractors]);
      const correctIndex = allChoices.indexOf(finalVal);

      const stimulusHtml = `
        <div class="iq-tally-board" role="region" aria-label="العمليات الحسابية المتتالية">
          <span class="iq-tally-step start">ابدأ بـ <strong>${start}</strong></span>
          <span class="iq-tally-arrow">←</span>
          <span class="iq-tally-step op-plus">+${op1}</span>
          <span class="iq-tally-arrow">←</span>
          <span class="iq-tally-step op-minus">-${op2}</span>
          <span class="iq-tally-arrow">←</span>
          <span class="iq-tally-step op-plus">+${op3}</span>
        </div>
      `;

      const options = allChoices.map((val, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: String(val),
        text_en: String(val)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `قم بإجراء العمليات المتتالية في ذهنك بالترتيب: ابدأ بـ (${start})، أضف ${op1}، ثم اطرح ${op2}، ثم أضف ${op3}. ما هي النتيجة النهائية؟`,
        questionText_en: `Perform the mental running calculation: Start with (${start}), add ${op1}, subtract ${op2}, then add ${op3}. What is the final total?`,
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `خطوات الحساب الذهني: ${start} + ${op1} = ${step1} ← ثم - ${op2} = ${step2} ← ثم + ${op3} = ${finalVal}.`,
        explanation_en: `Mental steps: ${start} + ${op1} = ${step1} -> - ${op2} = ${step2} -> + ${op3} = ${finalVal}.`
      };
    }
  },

  // 6. Mental Grid Rotation (2x2 Grid 90 deg rotation in mind)
  {
    id: 'memory_mental_grid_rotation',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // 2x2 Grid: [TL, TR, BL, BR]
      // 1 shaded cell e.g. Top-Right (TR)
      // Clockwise 90 deg rotation -> TR moves to BR!
      const cells = ['TL', 'TR', 'BL', 'BR'];
      const activeStart = prng.pick(cells);

      const rotMap = {
        'TL': 'TR',
        'TR': 'BR',
        'BR': 'BL',
        'BL': 'TL'
      };

      const correctEnd = rotMap[activeStart];

      const render2x2 = (activeCell) => {
        const coords = {
          'TL': { x: 7, y: 7 },
          'TR': { x: 37, y: 7 },
          'BL': { x: 7, y: 37 },
          'BR': { x: 37, y: 37 }
        };
        let cellsSvg = '';
        cells.forEach(c => {
          const isAct = c === activeCell;
          cellsSvg += `<rect x="${coords[c].x}" y="${coords[c].y}" width="26" height="26" rx="5" 
            fill="${isAct ? '#3b82f6' : 'rgba(148, 163, 184, 0.22)'}" 
            stroke="${isAct ? '#1d4ed8' : 'rgba(148, 163, 184, 0.45)'}" 
            stroke-width="${isAct ? '2' : '1.5'}"/>`;
          if (isAct) {
            cellsSvg += `<circle cx="${coords[c].x + 13}" cy="${coords[c].y + 13}" r="4" fill="#ffffff" opacity="0.95"/>`;
          }
        });
        return `<svg class="iq-grid-2x2-svg" viewBox="0 0 70 70" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="68" height="68" rx="8" fill="rgba(30, 41, 59, 0.12)" stroke="rgba(148, 163, 184, 0.4)" stroke-width="1.5"/>
          ${cellsSvg}
        </svg>`;
      };

      const stimulusHtml = `
        <div class="iq-rotation-stimulus" role="region" aria-label="الشكل الأصلي المطلوب تدويره">
          <div class="iq-rotation-grid-wrap">
            ${render2x2(activeStart)}
          </div>
          <div class="iq-rotation-info">
            <div class="iq-rotation-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
              <span>دوّر 90° مع عقارب الساعة (ذهنيًا)</span>
            </div>
            <p class="iq-rotation-hint">احتفظ بهذا الموضع في ذاكرتك ثم قم بتدويره 90 درجة في اتجاه عقارب الساعة لتحديد الموضع الناتج من بين الخيارات.</p>
          </div>
        </div>
      `;

      const allChoices = prng.shuffle([
        { cell: correctEnd, isCorrect: true },
        { cell: activeStart, isCorrect: false },
        { cell: rotMap[correctEnd], isCorrect: false },
        { cell: rotMap[rotMap[correctEnd]], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: render2x2(c.cell)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'احتفظ بالشكل في ذهنك ودوره بمقدار 90 درجة في اتجاه عقارب الساعة: أي خيار يمثل الموضع الصحيح بعد التدوير؟',
        questionText_en: 'Hold the 2x2 grid in mind and rotate it 90 degrees clockwise mentally: Which option depicts the correct resulting position?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: 'تدوير المربع بمقدار 90 درجة في اتجاه عقارب الساعة ينقل المربع المظلل إلى الموضع المقابل.',
        explanation_en: 'Rotating 90 degrees clockwise shifts the active cell to the next quadrant clockwise.'
      };
    }
  },

  // 7. Letter-Number Sorting Manipulation
  {
    id: 'memory_letter_number_sequencing',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // Input: jumbled mix [e.g. 5, B, 2, A]
      // Rule: Sort numbers in ascending order first, then letters in alphabetical order!
      // Output: 2, 5, A, B
      const nums = prng.sample([1, 2, 3, 4, 5, 6, 7, 8, 9], 2).sort((a, b) => a - b);
      const letters = prng.sample(['A', 'B', 'C', 'D', 'E'], 2).sort();

      const jumbled = prng.shuffle([nums[1], letters[0], nums[0], letters[1]]);
      const correctSorted = `${nums[0]} , ${nums[1]} , ${letters[0]} , ${letters[1]}`;

      const candidateDist = [
        `${letters[0]} , ${letters[1]} , ${nums[0]} , ${nums[1]}`,
        `${nums[1]} , ${nums[0]} , ${letters[0]} , ${letters[1]}`,
        `${nums[0]} , ${letters[0]} , ${nums[1]} , ${letters[1]}`,
        `${letters[1]} , ${letters[0]} , ${nums[1]} , ${nums[0]}`,
        `${nums[1]} , ${nums[0]} , ${letters[1]} , ${letters[0]}`
      ];
      const seenDist = new Set([correctSorted]);
      const distractors = [];
      for (const d of candidateDist) {
        if (!seenDist.has(d)) {
          seenDist.add(d);
          distractors.push(d);
          if (distractors.length === 3) break;
        }
      }

      const allChoices = prng.shuffle([correctSorted, ...distractors]);
      const correctIndex = allChoices.indexOf(correctSorted);

      const stimulusSvg = `<svg class="iq-svg-jumble" viewBox="0 0 320 80" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="320" height="80" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255, 255, 255, 0.12)"/>
        <text x="160" y="48" fill="#83C8F0" font-family="monospace, system-ui" font-size="22" font-weight="bold" text-anchor="middle">${jumbled.join('    ')}</text>
      </svg>`;

      const options = allChoices.map((s, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: s,
        text_en: s
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'قم بإعادة ترتيب العناصر ذهنيًا بحيث تأتي الأرقام أولاً تصاعديًا، تليها الحروف أبجديًا:',
        questionText_en: 'Reorganize these items mentally so that numbers appear first in ascending order, followed by letters in alphabetical order:',
        stimulusHtml: stimulusSvg,
        options,
        correctIndex,
        explanation_ar: `الترتيب المطلوب: الأرقام تصاعديًا (${nums[0]} ، ${nums[1]}) ثم الحروف أبجديًا (${letters[0]} ، ${letters[1]}).`,
        explanation_en: `Required arrangement: numbers ascending (${nums[0]}, ${nums[1]}) followed by letters alphabetically (${letters[0]}, ${letters[1]}).`
      };
    }
  },

  // 8. N-Back Spatial Match Probe
  {
    id: 'memory_n_back_spatial',
    domain: 'working_memory',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      // 4-step sequence of dot positions: [Pos1, Pos2, Pos3, Pos4]
      // Question: Did Pos 4 match Pos 2 (2-back)?
      const positions = ['يمين', 'يسار', 'أعلى', 'أسفل'];
      const posEn = ['Right', 'Left', 'Top', 'Bottom'];

      const step1 = prng.int(0, 3);
      const step2 = prng.int(0, 3);
      const step3 = (step2 + prng.int(1, 3)) % 4;
      const is2BackMatch = prng.next() > 0.5;
      const step4 = is2BackMatch ? step2 : (step2 + prng.int(1, 3)) % 4;

      const seqAr = [positions[step1], positions[step2], positions[step3], positions[step4]];
      const seqEn = [posEn[step1], posEn[step2], posEn[step3], posEn[step4]];

      const correctAns = is2BackMatch;

      const stimulusHtml = `
        <div class="iq-nback-board" role="region" aria-label="تسلسل الخطوات">
          ${seqAr.map((pos, i) => `
            <div class="iq-nback-step">
              <span class="iq-nback-step-num">${i + 1}</span>
              <span class="iq-nback-step-val">${pos}</span>
            </div>
          `).join('<span class="iq-nback-sep">←</span>')}
        </div>
      `;

      const allChoices = [
        { text_ar: 'نعم، الخطوة 4 تطابق الخطوة 2 تمامًا', text_en: 'Yes, Step 4 matches Step 2', isCorrect: is2BackMatch },
        { text_ar: 'لا، الخطوة 4 تختلف عن الخطوة 2', text_en: 'No, Step 4 differs from Step 2', isCorrect: !is2BackMatch }
      ];

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.text_ar,
        text_en: c.text_en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'في مهمة الذاكرة المعكوسة بخطوتين (2-Back): هل موضع الخطوة الرابعة يطابق نفس موضع الخطوة الثانية؟',
        questionText_en: 'In a 2-Back spatial memory task: Does the position in Step 4 match the exact position in Step 2?',
        stimulusHtml,
        options,
        correctIndex,
        explanation_ar: `الخطوة 2 كانت [${seqAr[1]}] والخطوة 4 كانت [${seqAr[3]}]، وبالتالي فإنهما ${is2BackMatch ? 'متطابقتان' : 'مختلفتان'}.`,
        explanation_en: `Step 2 was [${seqEn[1]}] and Step 4 was [${seqEn[3]}], meaning they ${is2BackMatch ? 'match' : 'differ'}.`
      };
    }
  }
];
